import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { 
  Trophy, 
  Target, 
  Calendar, 
  Flame, 
  Award,
  CheckCircle2,
  Circle,
  Loader2,
  Gift,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
  Brain,
  Users,
  Zap,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback OLQ habits in case API fails
const FALLBACK_OLQ_HABITS = [
  {"id": "olq_1", "name": "Effective Intelligence", "description": "Ability to apply knowledge practically", "activity": "Solve one logical puzzle or read a newspaper editorial and summarize it", "category": "Mental"},
  {"id": "olq_2", "name": "Reasoning Ability", "description": "Logical and analytical thinking", "activity": "Practice 5 reasoning questions (verbal/non-verbal)", "category": "Mental"},
  {"id": "olq_3", "name": "Organizing Ability", "description": "Plan and execute tasks systematically", "activity": "Create a to-do list and complete at least 3 tasks from it", "category": "Mental"},
  {"id": "olq_4", "name": "Power of Expression", "description": "Communicate thoughts clearly", "activity": "Write a 100-word essay on any current topic or practice a 2-minute speech", "category": "Social"},
  {"id": "olq_5", "name": "Social Adaptability", "description": "Adjust to different social situations", "activity": "Initiate a conversation with someone new or help a stranger", "category": "Social"},
  {"id": "olq_6", "name": "Cooperation", "description": "Work harmoniously with others", "activity": "Help a family member or colleague with their task today", "category": "Social"},
  {"id": "olq_7", "name": "Sense of Responsibility", "description": "Take ownership of duties", "activity": "Complete one pending responsibility without being reminded", "category": "Dynamic"},
  {"id": "olq_8", "name": "Initiative", "description": "Start action without external prompting", "activity": "Identify and start one task that needs to be done without anyone asking", "category": "Dynamic"},
  {"id": "olq_9", "name": "Self-Confidence", "description": "Believe in your abilities", "activity": "Stand in front of a mirror and practice confident body language for 5 minutes", "category": "Dynamic"},
  {"id": "olq_10", "name": "Speed of Decision", "description": "Make quick and effective decisions", "activity": "Set a 2-minute timer and make 3 decisions you've been postponing", "category": "Dynamic"},
  {"id": "olq_11", "name": "Ability to Influence", "description": "Positively affect others' actions", "activity": "Convince someone to try something healthy or positive today", "category": "Social"},
  {"id": "olq_12", "name": "Liveliness", "description": "Enthusiasm and energy in activities", "activity": "Do 15 minutes of physical exercise or engage in an energetic activity", "category": "Dynamic"},
  {"id": "olq_13", "name": "Determination", "description": "Persist despite obstacles", "activity": "Work on a challenging task for 30 minutes without giving up", "category": "Dynamic"},
  {"id": "olq_14", "name": "Courage", "description": "Face fears and challenges boldly", "activity": "Do one thing today that makes you slightly uncomfortable (speak up, try something new)", "category": "Dynamic"},
  {"id": "olq_15", "name": "Stamina", "description": "Physical and mental endurance", "activity": "Exercise for 20 minutes or practice meditation for 10 minutes", "category": "Physical"}
];

const categoryIcons = {
  Mental: Brain,
  Social: Users,
  Dynamic: Zap,
  Physical: Heart
};

const categoryColors = {
  Mental: 'bg-blue-500',
  Social: 'bg-purple-500',
  Dynamic: 'bg-orange-500',
  Physical: 'bg-green-500'
};

const DashboardPage = () => {
  const { user, loading: authLoading, updateUser, getStoredToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize with fallback habits so UI always has data
  const [olqHabits, setOlqHabits] = useState(FALLBACK_OLQ_HABITS);
  const [completedHabits, setCompletedHabits] = useState([]);
  const [stats, setStats] = useState({ total_points: 0, current_streak: 0, perfect_days: 0, points_to_discount: 50, discount_earned: false });
  const [todayLog, setTodayLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    Mental: true,
    Social: true,
    Dynamic: true,
    Physical: true
  });

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = getStoredToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (location.state?.user) {
      updateUser(location.state.user);
    }
  }, [location.state, updateUser]);

  useEffect(() => {
    // Don't redirect while auth is still loading
    if (authLoading) return;
    
    // If no user and auth finished loading, redirect to login
    if (!user) {
      navigate('/login');
      return;
    }
    
    // User is authenticated, fetch data
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    console.log('fetchData called, API_URL:', API_URL);
    
    // Always set fallback habits immediately so UI shows something
    setOlqHabits(FALLBACK_OLQ_HABITS);
    setStats({ total_points: 0, current_streak: 0, perfect_days: 0, points_to_discount: 50, discount_earned: false });
    
    const authHeaders = getAuthHeaders();
    
    try {
      // Fetch habits - no credentials needed for this public endpoint
      try {
        const habitsRes = await fetch(`${API_URL}/api/habits/olq-list`);
        console.log('Habits response status:', habitsRes.status);
        if (habitsRes.ok) {
          const fetchedHabits = await habitsRes.json();
          console.log('Fetched habits count:', fetchedHabits?.length);
          if (Array.isArray(fetchedHabits) && fetchedHabits.length > 0) {
            setOlqHabits(fetchedHabits);
          }
        }
      } catch (e) {
        console.log('Habits fetch error, using fallback:', e);
      }

      // Fetch stats - requires auth
      try {
        const statsRes = await fetch(`${API_URL}/api/habits/stats`, { 
          credentials: 'include',
          headers: authHeaders
        });
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }
      } catch (e) {
        console.log('Stats fetch failed');
      }
      
      // Fetch today's log
      try {
        const todayRes = await fetch(`${API_URL}/api/habits/today`, { 
          credentials: 'include',
          headers: authHeaders
        });
        if (todayRes.ok) {
          const todayData = await todayRes.json();
          setTodayLog(todayData);
          if (todayData?.completed_habits) {
            setCompletedHabits(todayData.completed_habits);
          }
        }
      } catch (e) {
        console.log('Today log fetch failed');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleHabit = (habitId) => {
    setCompletedHabits(prev => 
      prev.includes(habitId) 
        ? prev.filter(id => id !== habitId)
        : [...prev, habitId]
    );
  };

  const saveProgress = async () => {
    setSaving(true);
    const authHeaders = getAuthHeaders();
    
    try {
      const response = await fetch(`${API_URL}/api/habits/log`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...authHeaders
        },
        credentials: 'include',
        body: JSON.stringify({ completed_habits: completedHabits })
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.all_completed) {
          toast.success('🎖️ Perfect day! You earned 2 points!');
        } else {
          toast.success('Progress saved!');
        }
        
        // Check for milestone
        if (result.total_points >= 50 && stats?.total_points < 50) {
          toast.success('🏆 Congratulations! You earned 15% discount on 90-day programs!', {
            duration: 5000
          });
        }
        
        // Refresh stats
        const statsRes = await fetch(`${API_URL}/api/habits/stats`, { 
          credentials: 'include',
          headers: authHeaders
        });
        if (statsRes.ok) {
          const newStats = await statsRes.json();
          setStats(newStats);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Save failed:', response.status, errorData);
        toast.error(errorData.detail || 'Failed to save progress');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const groupedHabits = olqHabits.reduce((acc, habit) => {
    if (!acc[habit.category]) acc[habit.category] = [];
    acc[habit.category].push(habit);
    return acc;
  }, {});

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-cream-50 flex items-center justify-center">
          <Loader2 className="w-12 h-12 text-navy-900 animate-spin" />
        </div>
      </Layout>
    );
  }

  const completionPercentage = olqHabits.length > 0 
    ? Math.round((completedHabits.length / olqHabits.length) * 100) 
    : 0;

  return (
    <Layout>
      {/* Header Section */}
      <section className="bg-navy-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-gold-400 font-medium mb-1">Welcome back,</p>
              <h1 className="text-3xl font-bold text-white">{user?.name}</h1>
            </div>
            
            {/* Stats Cards */}
            <div className="flex flex-wrap gap-4">
              <div className="bg-white/10 rounded-xl px-6 py-4 text-center">
                <Trophy className="w-6 h-6 text-gold-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{stats?.total_points || 0}</p>
                <p className="text-gray-400 text-sm">Total Points</p>
              </div>
              <div className="bg-white/10 rounded-xl px-6 py-4 text-center">
                <Flame className="w-6 h-6 text-orange-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{stats?.current_streak || 0}</p>
                <p className="text-gray-400 text-sm">Day Streak</p>
              </div>
              <div className="bg-white/10 rounded-xl px-6 py-4 text-center">
                <Calendar className="w-6 h-6 text-olive-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{stats?.perfect_days || 0}</p>
                <p className="text-gray-400 text-sm">Perfect Days</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - Habit Tracker */}
          <div className="lg:col-span-2 space-y-6">
            {/* Today's Progress */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl text-navy-900">Today's OLQ Progress</CardTitle>
                  <span className="text-sm text-gray-500">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">{completedHabits.length} of {olqHabits.length} habits completed</span>
                    <span className="text-sm font-semibold text-navy-900">{completionPercentage}%</span>
                  </div>
                  <Progress value={completionPercentage} className="h-3" />
                  {completionPercentage === 100 && (
                    <p className="text-olive-600 text-sm mt-2 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" />
                      All habits completed! You'll earn 2 points.
                    </p>
                  )}
                </div>

                {/* Habits by Category */}
                <div className="space-y-4">
                  {Object.entries(groupedHabits).map(([category, habits]) => {
                    const CategoryIcon = categoryIcons[category] || Target;
                    const completedInCategory = habits.filter(h => completedHabits.includes(h.id)).length;
                    
                    return (
                      <div key={category} className="border rounded-xl overflow-hidden">
                        <button
                          onClick={() => toggleCategory(category)}
                          className={`w-full flex items-center justify-between p-4 ${categoryColors[category]} bg-opacity-10 hover:bg-opacity-20 transition-colors`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg ${categoryColors[category]} flex items-center justify-center`}>
                              <CategoryIcon className="w-4 h-4 text-white" />
                            </div>
                            <span className="font-semibold text-navy-900">{category}</span>
                            <span className="text-sm text-gray-500">({completedInCategory}/{habits.length})</span>
                          </div>
                          {expandedCategories[category] ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        
                        {expandedCategories[category] && (
                          <div className="divide-y">
                            {habits.map((habit) => (
                              <div 
                                key={habit.id}
                                className={`p-4 flex items-start gap-4 hover:bg-gray-50 transition-colors cursor-pointer ${completedHabits.includes(habit.id) ? 'bg-olive-50' : ''}`}
                                onClick={() => toggleHabit(habit.id)}
                              >
                                <div className="pt-1">
                                  {completedHabits.includes(habit.id) ? (
                                    <CheckCircle2 className="w-6 h-6 text-olive-600" />
                                  ) : (
                                    <Circle className="w-6 h-6 text-gray-300" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <h4 className={`font-medium ${completedHabits.includes(habit.id) ? 'text-olive-700 line-through' : 'text-navy-900'}`}>
                                    {habit.name}
                                  </h4>
                                  <p className="text-sm text-gray-500 mt-1">{habit.description}</p>
                                  <p className="text-sm text-olive-600 mt-2 bg-olive-50 rounded-lg px-3 py-2">
                                    📌 {habit.activity}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <Button 
                  onClick={saveProgress}
                  disabled={saving}
                  className="w-full mt-6 bg-navy-900 hover:bg-navy-800 text-white font-semibold py-6"
                >
                  {saving ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><CheckCircle2 className="w-5 h-5 mr-2" /> Save Today's Progress</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Points Progress */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-navy-900 to-navy-800 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Trophy className="w-8 h-8 text-gold-400" />
                  <h3 className="text-lg font-bold">Points Progress</h3>
                </div>
                
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-300">Progress to 15% discount</span>
                    <span className="text-gold-400 font-bold">{stats?.total_points || 0}/50</span>
                  </div>
                  <Progress value={Math.min(((stats?.total_points || 0) / 50) * 100, 100)} className="h-3 bg-white/20" />
                </div>
                
                {stats?.discount_earned ? (
                  <div className="bg-gold-500/20 rounded-xl p-4 border border-gold-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Gift className="w-5 h-5 text-gold-400" />
                      <span className="font-bold text-gold-400">15% Discount Earned!</span>
                    </div>
                    <p className="text-sm text-gray-300 mb-3">
                      Congratulations! Contact us to claim your discount on 90-day programs.
                    </p>
                    <div className="space-y-2">
                      <a href="tel:7204064650" className="flex items-center gap-2 text-gold-300 hover:text-gold-200 text-sm">
                        <Phone className="w-4 h-4" /> 7204064650
                      </a>
                      <a href="mailto:info@jaydefenceacademy.com" className="flex items-center gap-2 text-gold-300 hover:text-gold-200 text-sm">
                        <Mail className="w-4 h-4" /> info@jaydefenceacademy.com
                      </a>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-300">
                    Complete all {olqHabits.length} habits daily to earn 2 points. 
                    {stats?.points_to_discount > 0 && ` ${stats.points_to_discount} points to discount!`}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* How it Works */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-navy-900 mb-4">How It Works</h3>
                <div className="space-y-4">
                  {[
                    { icon: Target, text: 'Complete activities for each OLQ daily' },
                    { icon: CheckCircle2, text: 'Check off habits as you complete them' },
                    { icon: Award, text: 'Earn 2 points for completing all 15 habits' },
                    { icon: Gift, text: 'Get 15% discount at 50 points' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-cream-100 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-navy-900" />
                      </div>
                      <p className="text-sm text-gray-600 pt-1">{item.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-navy-900 mb-4">Quick Links</h3>
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => navigate('/mock-exams')}
                  >
                    📝 Take NDA Mock Exam
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => navigate('/courses')}
                  >
                    📚 View Our Courses
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => navigate('/contact')}
                  >
                    📞 Contact Us
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
